import ArticleSelectorDialog from 'components/Common/ArticleSelector/ArticleSelectorDialog'
import { AssemblySimplificationDialog } from 'components/Common/AssemblySimplification/AssemblySimplificationDialog'
import { WorkingStepMultipleItemsDialog } from 'components/ProjectPage/BillOfMaterials/Rows/Components/RowWorkingSteps/WorkingSteps/WorkingStepMultipleItemsDialog'
import { BatchEditRows } from 'features/BillOfMaterials/BatchEditRowsDialog/BatchEditRowsDialog'
import { ReCreateProjectConfirmationDialog } from 'features/BillOfMaterials/BoMItemMenu/MenuItems/ReCreateProjectMenuItem'
import { CuttingPlanDialog } from 'features/BillOfMaterials/MaterialHeader/ActiveCuttingPlanDialog'
import { BomItemDetailsDrawer } from 'features/BomItemDetailsDrawer/BomItemDetailsDrawer'
import { ConfirmationDialog } from 'features/ConfirmationDialog/ConfirmationDialog'
import { CopyPasteMaterialDrawer } from 'features/CopyAndPasteMaterials/CopyPasteMaterialDrawer'
import { CostPriceDetailsDialog } from 'features/CostPriceDetails/CostPriceDetailsDialog'
import { EventLogsDialog } from 'features/EventLogs/components/EventLogsDialog'
import { ProjectOperationsDialog } from 'features/GeometryLogs/ProjectOperationsLogsDrawer'
import { ProjectProvideRawMaterialSettingsDialog } from 'features/MaterialProvidedSettings/Project/ProjectProvideRawMaterialSettingsDialog'
import { ProjectDetailsDialog } from 'features/ProjectDetailsInfo/components/ProjectDetailsInfoDialog'
import { PurchasingPartDialog } from 'features/PurchasingPartDetails/PurchasingPartDialog'
import { SalesRepresentativeDialog } from 'features/SalesRepresentative/components/SalesRepresentativeDialog'
import { SupportContactDialog } from 'features/SupportContactDialog/SupportContactDialog'
import React from 'react'
import { ModalTypes } from 'store/UI/UITypes'
import { RootAppState, useAppSelector } from 'store/configureStore'
import { useDialogManager, useModalStackStore } from './useDialogManager'

const getDialogComponent = (args: {
  modalType: ModalTypes
  modalProps: any
  onClose: () => void
}) => {
  const modals: Record<ModalTypes, () => React.ReactElement<any>> = {
    ArticleSelector: () => (
      <ArticleSelectorDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    AssemblySimplification: () => (
      <AssemblySimplificationDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    BatchEditBomItems: () => (
      <BatchEditRows {...args.modalProps} open onClose={args.onClose} />
    ),
    CuttingPlanDetails: () => (
      <CuttingPlanDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    BomItemDetailsDrawer: () => (
      <BomItemDetailsDrawer {...args.modalProps} open onClose={args.onClose} />
    ),
    ConfirmationDialog: () => (
      <ConfirmationDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    CostPriceDetails: () => (
      <CostPriceDetailsDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    EventLogs: () => (
      <EventLogsDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    ProjectOperations: () => (
      <ProjectOperationsDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    SalesRepresentative: () => (
      <SalesRepresentativeDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    SupportContact: () => (
      <SupportContactDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    PurchasingPartDialog: () => (
      <PurchasingPartDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    ProjectDetailsDialog: () => (
      <ProjectDetailsDialog {...args.modalProps} open onClose={args.onClose} />
    ),
    CopyPasteDrawer: () => (
      <CopyPasteMaterialDrawer
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    ProjectMaterialEstimationSettingsDialog: () => (
      <ProjectProvideRawMaterialSettingsDialog
        {...args.modalProps}
        open
        onClose={args.onClose}
      />
    ),
    ReCreateProjectConfirmationDialog: () => {
      return <ReCreateProjectConfirmationDialog open onClose={args.onClose} />
    },
    WorkingStepMultipleItemsDialog: () => {
      return (
        <WorkingStepMultipleItemsDialog
          open
          onClose={args.onClose}
          {...args.modalProps}
        />
      )
    },
  }

  return modals[args.modalType]
}

export const DialogManager = () => {
  const activeModal = useAppSelector((state: RootAppState) => state.ui.Modals)

  const modalStackStore = useModalStackStore()

  const { closeDialog, openDialog } = useDialogManager()

  const dialogFactory = getDialogComponent({
    modalType: activeModal?.CurrentModal,
    modalProps: activeModal?.ModalProps,
    onClose: () => {
      activeModal?.ModalProps?.onClose?.()
      closeDialog()
    },
  })

  if (dialogFactory && activeModal) {
    // push to opened modals stack
    if (
      !modalStackStore.modalStack.some(
        (modal) => modal.modalType === activeModal.CurrentModal,
      )
    ) {
      modalStackStore.addModal(activeModal.CurrentModal, activeModal.ModalProps)
    }

    return dialogFactory()
  } else if (modalStackStore.modalStack.length > 0) {
    // pop from opened modals stack (this will be the currently visible modal)
    // let lastOpenedModal = openedModalsStack.pop()
    const removedModalType = modalStackStore.removeCurrentModal()

    const modalToOpen =
      modalStackStore.modalStack[modalStackStore.modalStack.length - 1]
    if (modalToOpen) {
      openDialog(modalToOpen.modalType, modalToOpen.modalProps, false)
    }
  }

  return null
}
